/**
 * Blog Service
 * Handles all blog-related API calls for Admin
 */

import { adminGet, adminPost, adminPut, adminDelete, adminUpload } from './admin-ajax';

// Types
export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt?: string;
  content?: string;
  status: string;
  category?: string;
  tags: string[];
  author?: {
    name: string;
    email: string;
    avatar?: string;
  };
  featured_image?: string;
  seo_title?: string;
  seo_description?: string;
  published_at?: number;
  created_at?: number;
  updated_at?: number;
  read_time?: number;
}

export interface BlogPostListResponse {
  posts: BlogPost[];
  total: number;
  page: number;
  limit: number;
  has_more: boolean;
}

export interface CreateBlogPostRequest {
  title: string;
  content: string;
  slug?: string;
  author_name?: string;
  author_email?: string;
  category?: string;
  tags?: string[];
  excerpt?: string;
  featured_image?: string;
  author_avatar?: string;
  seo_title?: string;
  seo_description?: string;
  auto_publish?: boolean;
}

export interface BatchCreateBlogPostsRequest {
  posts: CreateBlogPostRequest[];
  auto_publish?: boolean;
}

export interface UpdateBlogPostRequest {
  title?: string;
  content?: string;
  excerpt?: string;
  category?: string;
  tags?: string[];
  featured_image?: string;
  seo_title?: string;
  seo_description?: string;
}

export interface BlogCoverImage {
  id: string;
  blob_key?: string;
  url: string;
  created_at?: number | null;
}

export interface FailedPost {
  index: number;
  title: string;
  error: string;
}

export interface BatchCreateResponse {
  success: boolean;
  message: string;
  created_count: number;
  failed_count: number;
  created_posts: BlogPost[];
  failed_posts: FailedPost[];
}

class AdminBlogService {
  private baseUrl = '/api/admin/blog';

  // Posts
  async listPosts(params: {
    page?: number;
    limit?: number;
    status?: string;
    category?: string;
    search?: string;
  } = {}): Promise<BlogPostListResponse> {
    return adminGet<BlogPostListResponse>(`${this.baseUrl}/posts`, params);
  }

  async getPost(postId: string): Promise<{ success: boolean; post: BlogPost }> {
    return adminGet<{ success: boolean; post: BlogPost }>(`${this.baseUrl}/posts/${postId}`);
  }

  async createPost(data: CreateBlogPostRequest): Promise<{ success: boolean; message: string; post: BlogPost }> {
    return adminPost<{ success: boolean; message: string; post: BlogPost }>(`${this.baseUrl}/posts/create`, data);
  }

  async batchCreatePosts(data: BatchCreateBlogPostsRequest): Promise<BatchCreateResponse> {
    return adminPost<BatchCreateResponse>(`${this.baseUrl}/posts/batch_create`, data);
  }

  async updatePost(postId: string, data: UpdateBlogPostRequest): Promise<{ success: boolean; message: string; post: BlogPost }> {
    return adminPut<{ success: boolean; message: string; post: BlogPost }>(`${this.baseUrl}/posts/${postId}`, data);
  }

  async deletePost(postId: string): Promise<{ success: boolean; message: string }> {
    return adminDelete<{ success: boolean; message: string }>(`${this.baseUrl}/posts/${postId}`);
  }

  async publishPost(postId: string): Promise<{ success: boolean; message: string; post: BlogPost }> {
    return adminPost<{ success: boolean; message: string; post: BlogPost }>(`${this.baseUrl}/posts/${postId}/publish`);
  }

  async unpublishPost(postId: string): Promise<{ success: boolean; message: string; post: BlogPost }> {
    return adminPost<{ success: boolean; message: string; post: BlogPost }>(`${this.baseUrl}/posts/${postId}/unpublish`);
  }

  async batchPublish(ids: string[]): Promise<{ success: boolean; message: string; success_count: number; failed_count: number }> {
    return adminPost<{ success: boolean; message: string; success_count: number; failed_count: number }>(`${this.baseUrl}/posts/batch_publish`, { ids });
  }

  async batchUnpublish(ids: string[]): Promise<{ success: boolean; message: string; success_count: number; failed_count: number }> {
    return adminPost<{ success: boolean; message: string; success_count: number; failed_count: number }>(`${this.baseUrl}/posts/batch_unpublish`, { ids });
  }

  async batchDelete(ids: string[]): Promise<{ success: boolean; message: string; success_count: number; failed_count: number }> {
    return adminPost<{ success: boolean; message: string; success_count: number; failed_count: number }>(`${this.baseUrl}/posts/batch_delete`, { ids });
  }

  // Covers
  async listCovers(): Promise<{ success: boolean; covers: BlogCoverImage[] }> {
    return adminGet<{ success: boolean; covers: BlogCoverImage[] }>(`${this.baseUrl}/covers`);
  }

  async uploadCovers(
    files: File[],
    onProgress?: (percent: number) => void
  ): Promise<{
    success: boolean;
    uploaded: Array<{ id: string; blob_key?: string; url: string; error?: string }>;
  }> {
    if (!files || files.length === 0) {
      throw new Error('No files provided');
    }

    // Single file upload - use adminUpload directly
    if (files.length === 1) {
      return adminUpload<{
        success: boolean;
        uploaded: Array<{ id: string; blob_key?: string; url: string; error?: string }>;
      }>(`${this.baseUrl}/covers_upload`, files, 'files', onProgress);
    }

    // Multiple files - upload concurrently with progress tracking
    const concurrency = 8;
    const results: Array<{ id: string; blob_key?: string; url: string; error?: string }> = new Array(files.length);

    let nextIndex = 0;
    let completed = 0;

    const updateOverallProgress = () => {
      if (!onProgress) return;
      const percent = Math.max(0, Math.min(100, Math.round((completed / files.length) * 100)));
      onProgress(percent);
    };

    const worker = async () => {
      while (true) {
        const idx = nextIndex;
        nextIndex += 1;
        if (idx >= files.length) return;

        try {
          const res = await adminUpload<{
            success: boolean;
            uploaded: Array<{ id: string; blob_key?: string; url: string; error?: string }>;
          }>(`${this.baseUrl}/covers_upload`, [files[idx]], 'files');
          const first = (res.uploaded || [])[0];
          if (first) {
            results[idx] = first;
          } else {
            results[idx] = { id: '', url: '', error: 'Upload failed' };
          }
        } catch (error: unknown) {
          const errorMessage = error instanceof Error ? error.message : 'Upload failed';
          results[idx] = { id: '', url: '', error: errorMessage };
        } finally {
          completed += 1;
          updateOverallProgress();
        }
      }
    };

    const workerCount = Math.min(concurrency, files.length);
    await Promise.all(new Array(workerCount).fill(0).map(() => worker()));
    if (onProgress) onProgress(100);

    return { success: true, uploaded: results };
  }

  async deleteCover(coverId: string): Promise<{ success: boolean; message?: string }> {
    return adminDelete<{ success: boolean; message?: string }>(`${this.baseUrl}/covers/${coverId}`);
  }
}

export const adminBlogService = new AdminBlogService();
